/**
 * <neroli-popup> — promotional popup.
 *
 * Deliberately the opposite of the age gate in every way that matters:
 * Escape closes it, the close button is always reachable, and focus returns
 * where it came from. A marketing popup that traps someone is a dark pattern,
 * and Theme Store review treats it as one.
 *
 * It also waits for the age gate. A promo stacked on top of a compliance gate
 * is both a broken experience and a compliance problem.
 */

const FOCUSABLE = 'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])';
const MOBILE = '(max-width: 749px)';

class NeroliPopup extends HTMLElement {
  connectedCallback() {
    this.panel = this.querySelector('[data-popup-panel]');
    this.isEditor = this.dataset.editor === 'true';
    this.storageKey = this.dataset.storageKey;
    this.dismissDays = parseInt(this.dataset.dismissDays, 10);

    if (this.isEditor) {
      this.setupEditorPreview();
      return;
    }

    // A successful signup re-renders the page with the form in its success
    // state. Reopening immediately is the only way the visitor sees the
    // confirmation, and the discount code, at all.
    if (this.querySelector('[data-popup-success]')) {
      this.show();
      this.bind();
      return;
    }

    if (this.isDismissed()) return;
    if (!this.matchesViewport()) return;

    this.bind();
    this.waitForAgeGate(() => this.arm());
  }

  disconnectedCallback() {
    clearTimeout(this.timer);
    document.removeEventListener('focusin', this.enforceFocus);
    window.removeEventListener('scroll', this.onScroll);
    document.removeEventListener('mouseout', this.onExit);
  }

  bind() {
    this.addEventListener('click', this.onClick);
    this.addEventListener('keydown', this.onKeydown);
  }

  /* ---- eligibility ---- */

  matchesViewport() {
    if (this.dataset.showMobile === 'true') return true;
    return !window.matchMedia(MOBILE).matches;
  }

  isDismissed() {
    try {
      const record = localStorage.getItem(this.storageKey);
      if (!record) return false;
      // "0 days" is stored as 'never', meaning show once and never again.
      if (record === 'never') return true;
      return Date.now() < Number(record);
    } catch (e) {
      // Private browsing can throw on access. Failing open shows the popup,
      // which is better than a silent breakage the merchant cannot see.
      return false;
    }
  }

  recordDismissal() {
    try {
      const value = this.dismissDays === 0
        ? 'never'
        : String(Date.now() + this.dismissDays * 864e5);
      localStorage.setItem(this.storageKey, value);
    } catch (e) {
      /* nothing to do if storage is unavailable */
    }
  }

  // The age gate removes itself from the DOM once answered, so the popup
  // polls briefly rather than assuming either order of loading.
  waitForAgeGate(next) {
    const gate = document.querySelector('neroli-age-gate:not([hidden])');
    if (!gate) return next();

    const observer = new MutationObserver(() => {
      if (!document.querySelector('neroli-age-gate:not([hidden])')) {
        observer.disconnect();
        next();
      }
    });
    observer.observe(document.body, { childList: true, subtree: true, attributes: true });
  }

  /* ---- triggers ---- */

  arm() {
    switch (this.dataset.trigger) {
      case 'immediate':
        this.show();
        break;
      case 'scroll':
        window.addEventListener('scroll', this.onScroll, { passive: true });
        break;
      case 'exit':
        // No cursor on touch, so there is no leaving gesture to detect.
        // Falls back to a delay rather than never showing at all.
        if (window.matchMedia(MOBILE).matches) {
          this.timer = setTimeout(() => this.show(), 20000);
        } else {
          document.addEventListener('mouseout', this.onExit);
        }
        break;
      default:
        this.timer = setTimeout(
          () => this.show(),
          (parseInt(this.dataset.delay, 10) || 15) * 1000
        );
    }
  }

  onScroll = () => {
    const scrolled = window.scrollY + window.innerHeight;
    const target = document.body.scrollHeight * (Number(this.dataset.scrollPercent) / 100);
    if (scrolled >= target) {
      window.removeEventListener('scroll', this.onScroll);
      this.show();
    }
  };

  onExit = (event) => {
    // relatedTarget is null when the cursor leaves the document entirely,
    // rather than moving between elements inside it.
    if (event.relatedTarget || event.clientY > 0) return;
    document.removeEventListener('mouseout', this.onExit);
    this.show();
  };

  /* ---- open / close ---- */

  show() {
    if (this.shown) return;
    this.shown = true;
    this.hidden = false;

    this.previousFocus = document.activeElement;

    // A bottom bar does not cover the page, so it should not steal focus or
    // trap it. Only the modal positions do.
    if (this.panel.getAttribute('aria-modal') === 'true') {
      const first = this.panel.querySelector(FOCUSABLE);
      if (first) first.focus();
      document.addEventListener('focusin', this.enforceFocus);
    }
  }

  close() {
    this.recordDismissal();
    document.removeEventListener('focusin', this.enforceFocus);
    this.hidden = true;
    if (this.previousFocus && this.previousFocus.focus) this.previousFocus.focus();
  }

  enforceFocus = (event) => {
    if (this.hidden || this.contains(event.target)) return;
    const first = this.panel.querySelector(FOCUSABLE);
    if (first) first.focus();
  };

  onKeydown = (event) => {
    if (event.key === 'Escape') this.close();
  };

  onClick = (event) => {
    if (event.target.closest('[data-popup-close]')) return this.close();
    if (event.target.closest('[data-popup-copy]')) return this.copyCode(event);
    // Clicking the dimmed area behind the panel is a close gesture people
    // expect; clicks inside the panel must not bubble into it.
    if (event.target.closest('[data-popup-backdrop]')) return this.close();
  };

  async copyCode(event) {
    const button = event.target.closest('[data-popup-copy]');
    const code = this.querySelector('[data-popup-code]');
    if (!code) return;

    try {
      await navigator.clipboard.writeText(code.textContent.trim());
      const original = button.textContent;
      button.textContent = button.dataset.copiedLabel;
      setTimeout(() => {
        button.textContent = original;
      }, 2000);
    } catch (e) {
      // Clipboard access can be refused. Selecting the code lets the visitor
      // copy it themselves rather than leaving a button that does nothing.
      const range = document.createRange();
      range.selectNodeContents(code);
      const selection = window.getSelection();
      selection.removeAllRanges();
      selection.addRange(range);
    }
  }

  /* ---- theme editor ---- */

  // Shows only while the section is selected, so it does not sit on top of
  // everything else the merchant is trying to edit.
  setupEditorPreview() {
    this.bind();
    this.close = () => {
      this.hidden = true;
    };

    document.addEventListener('shopify:section:select', (event) => {
      if (event.target.contains(this)) {
        this.shown = false;
        this.show();
      }
    });
    document.addEventListener('shopify:section:deselect', (event) => {
      if (event.target.contains(this)) this.hidden = true;
    });
    document.addEventListener('shopify:block:select', (event) => {
      if (event.target.closest('neroli-popup') === this) {
        this.shown = false;
        this.show();
      }
    });
  }
}

if (!customElements.get('neroli-popup')) {
  customElements.define('neroli-popup', NeroliPopup);
}
